// src/components/quiz/ModeSelectLanding.js
// Landing ridisegnata: 3 card modalità (record personale + "Gioca" per
// ciascuna) e un pannello classifica con un tab per modalità, dato che
// punteggi/meccaniche diverse non sono confrontabili tra loro.
import React, { useState } from 'react';
import { Trophy, Zap, Timer, HelpCircle, Loader2 } from 'lucide-react';
import { isSamePerson } from '../../firebase/quizService';
import { fmtTime, RankMedal, shortAccountCode } from './quizShared';

export const MODES = [
  { key: 'survival', icon: Zap, title: 'Sopravvivenza', tagline: 'Rispondi finché non sbagli: quanto lontano arrivi?' },
  { key: 'timeattack', icon: Timer, title: 'Time Attack', tagline: '15 domande, 90 secondi: fai più punti possibile.' },
  { key: 'guess', icon: HelpCircle, title: 'Indovina il giocatore', tagline: 'Indizi progressivi: meno ne usi, più punti fai.' },
];

function ModeCard({ mode, personalBest, loadingData, onStart }) {
  const Icon = mode.icon;
  return (
    <div className="quiz-mode-card glass">
      <div className="quiz-mode-card__icon"><Icon size={22} /></div>
      <h3>{mode.title}</h3>
      <p>{mode.tagline}</p>
      {personalBest ? (
        <div className="quiz-mode-card__best">
          <span>Record personale</span>
          <strong>{personalBest.score} pt</strong>
        </div>
      ) : (
        <div className="quiz-mode-card__best quiz-mode-card__best--empty">
          <span>Ancora nessuna partita</span>
        </div>
      )}
      <button className="quiz-start-btn quiz-start-btn--card" onClick={() => onStart(mode.key)} disabled={loadingData}>
        {loadingData ? <><Loader2 size={15} className="spin" /> Caricamento...</> : <><Icon size={14} /> Gioca</>}
      </button>
    </div>
  );
}

export default function ModeSelectLanding({
  onStart, loadingData,
  personalBests, leaderboards, loadingScores, scoreError,
  user,
}) {
  const [activeTab, setActiveTab] = useState('survival');
  const activeLeaderboard = leaderboards?.[activeTab] || [];

  return (
    <div className="quiz-landing">
      <div className="quiz-hero glass">
        <div className="quiz-hero__glow" />
        <div className="quiz-hero__icon"><Zap size={28} /></div>
        <h1>FantaQuizz!</h1>
        <p>Metti alla prova la tua conoscenza del fantacalcio.<br />Scegli una modalità per iniziare.</p>
      </div>

      <div className="quiz-mode-cards">
        {MODES.map((mode) => (
          <ModeCard
            key={mode.key}
            mode={mode}
            personalBest={personalBests?.[mode.key] || null}
            loadingData={loadingData}
            onStart={onStart}
          />
        ))}
      </div>

      <div className="quiz-panel glass quiz-leaderboard-panel">
        <div className="quiz-panel__head">
          <Trophy size={15} />
          <h2>Classifica</h2>
        </div>

        <div className="quiz-lb-tabs" role="tablist">
          {MODES.map((mode) => (
            <button
              key={mode.key}
              type="button"
              role="tab"
              aria-selected={activeTab === mode.key}
              className={`quiz-lb-tab ${activeTab === mode.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(mode.key)}
            >
              {mode.title}
            </button>
          ))}
        </div>

        {loadingScores ? (
          <div className="quiz-empty"><Loader2 size={18} className="spin" /></div>
        ) : scoreError ? (
          <div className="quiz-empty" style={{ color: 'var(--danger, #f55)' }}>
            <p style={{ fontSize: '0.75rem' }}>Errore: {scoreError}</p>
          </div>
        ) : activeLeaderboard.length === 0 ? (
          <div className="quiz-empty"><p>Ancora nessun punteggio registrato per questa modalità.</p></div>
        ) : (
          <div className="quiz-leaderboard">
            {(() => {
              // Conta i nomi duplicati: il codice account compare solo quando
              // due persone diverse condividono lo stesso nome visualizzato.
              const nameCounts = activeLeaderboard.reduce((acc, e) => {
                const key = String(e.displayName || 'Anonimo').trim().toLowerCase();
                acc[key] = (acc[key] || 0) + 1;
                return acc;
              }, {});
              return activeLeaderboard.map((entry, idx) => {
                const nameKey = String(entry.displayName || 'Anonimo').trim().toLowerCase();
                const showCode = nameCounts[nameKey] > 1;
                return (
                  <div
                    key={entry.id}
                    className={`quiz-lb-row ${user && isSamePerson(entry, { userId: user.uid, email: user.email }) ? 'quiz-lb-row--me' : ''}`}
                  >
                    <div className="quiz-lb-rank"><RankMedal rank={idx} /></div>
                    <div className="quiz-lb-info">
                      <strong>
                        {entry.displayName || 'Anonimo'}
                        {showCode && <span className="quiz-lb-code">#{shortAccountCode(entry)}</span>}
                      </strong>
                      <span>
                        {entry.correctCount} corrette
                        {Number.isFinite(entry.durationSecs) ? ` · in ${fmtTime(entry.durationSecs)}` : ''}
                      </span>
                    </div>
                    <div className="quiz-lb-score">{entry.score} <span>pt</span></div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
